import {get} from "svelte/store";
import {cartItemsStore, cartTotalStore, clearCart} from "../cart/cart-store.js";
import {publishConfigStore, tenantKeyStore} from "../core/app-store.js";
import {
    additionalCostsStore,
    clearOrderPlacementResults,
    deliveryStore,
    orderConfigStore,
    orderFormFieldsStore,
    setOrderPlacementResults
} from "./orders-store.js";
import {reachGoal} from "../core/ecommerce.js";
import {eventBus} from "../core/event-bus-store.js";
import {closeOrderScreen, openOrderPlacementResultScreen} from "../core/screens-manager.js";
import {getMenuId} from "../core/get-menu-id.js";
import {getTable} from "../core/table-store.js";
import {instantTranslate} from "../core/i18n/language-store.js";

const orderConfig = get(orderConfigStore);

/**
 *
 * @param items
 * @param total: {sum: number, count: number}
 * @param params
 * @param delivery
 * @returns {{orderDetails: {customFieldValuesMap, pickupPoint: (*|undefined), totalPrice, contact: {address: (*|undefined), phone, name}, comment, currency, onsite, totalCount, items: {itemId: *, mods: *, menuKey: *, price: *, name: *, count: *, weight: *, discount: *}[]}, tenantKey: {}, menuId}}
 */
function getOrder (items, total, params, delivery, deliveryByRegion, deliveryByTotal) {

    const publishConfig = get(publishConfigStore);
    const tenantKey = get(tenantKeyStore);
    const menuId = getMenuId(publishConfig);
    const table = getTable();

    const totals = getTotals();

    return {
        tenantKey,
        menuId: menuId,
        orderDetails: {
            table: table,
            contact: {
                name: params.name,
                phone: params.phone,
                address: delivery.selectedDelivery ? params.address : undefined,
                deliveryRegion: delivery.deliveryRegion
            },
            customFieldValuesMap: params.customFieldValuesMap,
            comment: params.comment,
            items: (items || []).map(x => {
                return {
                    menuKey: x.menuKey,
                    itemId: x.id,
                    name: x.name,
                    count: x.count,
                    price: x.price,
                    weight: x.weight,
                    mods: x.mods,
                    discount: x.discount,
                }
            }),
            additionalCosts: get(additionalCostsStore),
            onsite: delivery.selectedOnsite,
            pickupPoint: delivery.selectedPickup ? delivery.selectedPickupPoint?.address : undefined,
            deliveryPrice: totals.deliveryPrice,
            itemsPrice: totals.itemsPrice,
            totalPrice: totals.totalPrice,
            totalCount: total.count,
            currency: publishConfig.currency
        }
    };
}

/**
 *
 * @param delivery - selected delivery options
 * @param deliveryRegion - order form data
 * @param orderConfig - delivery options
 * @returns {number}
 */
export function getDeliveryPriceByRegion (delivery, deliveryRegion, orderConfig) {

    deliveryRegion = (deliveryRegion || '').trim();
    const deliveryPriceRules = orderConfig.deliveryPriceRules || [];

    let price = 0;

    deliveryPriceRules.forEach(rule => {

        if (rule.type === 'region' && rule.value.regionId === deliveryRegion && delivery.selectedDelivery) {
            price = rule.value.price || 0;
            // console.log(rule);
        }
    });

    return price;
}

/**
 *
 * @param total
 * @param orderConfig - delivery options
 * @returns {number}
 */
function getDeliveryPriceByTotal (total, orderConfig) {

    const orderTotal = total.sum;
    const deliveryPriceRules = orderConfig.deliveryPriceRules || [];

    let price = 0;

    deliveryPriceRules.forEach(rule => {

        if (rule.type === 'total' && rule.value.total.length > 0) {

            const min = rule.value.total[0] || 0;
            const max = rule.value.total[1] || orderTotal;

            if (orderTotal > min && orderTotal <= max) {
                price = rule.value.price || 0;
                // console.log(rule);
            }
        }
    });

    return price;
}

/**
 * Calculates final delivery price based on rules according to algorithm (max, min, avg)
 *
 * @param delivery
 * @param total
 * @param deliveryRegion
 * @param orderConfig
 * @returns {number}
 */
function getFinalDeliveryPrice (delivery, total, deliveryRegion, orderConfig) {

    const byTotal = getDeliveryPriceByTotal(total, orderConfig);
    const byRegion = getDeliveryPriceByRegion(delivery, deliveryRegion, orderConfig);

    orderConfig.deliveryPriceCombination  = orderConfig.deliveryPriceCombination || 'max';

    let price;
    if (orderConfig.deliveryPriceCombination === 'max') {
        price = Math.max(byTotal, byRegion);
    }

    if (orderConfig.deliveryPriceCombination === 'add') {
        price = byTotal + byRegion;
    }

    if (orderConfig.deliveryPriceCombination === 'min') {
        price = Math.min(byTotal, byRegion);
    }

    return price;
}

/**
 * Get total prices incl. Items price (cost of all items), Delivery price (dep. on delivery rules) and Total price (summary)
 *
 * @returns {{itemsPrice: number, totalPrice: number, deliveryPrice: number}}
 */
export function getTotals () {

    const delivery = get(deliveryStore);
    const cartTotal = get(cartTotalStore);
    const additionalCosts = get(additionalCostsStore);

    const itemsPrice = cartTotal.sum;
    const deliveryPrice = delivery.selectedDelivery ? getFinalDeliveryPrice(delivery, cartTotal, delivery.deliveryRegion, orderConfig) : 0;

    return {
        itemsPrice,
        deliveryPrice,
        totalPrice: itemsPrice + deliveryPrice + additionalCosts.total
    };
}

export function validateOrderForm (params, orderConfig, orderFormFieldsConfig, delivery) {

    const requiredCustomFieldKeys = (orderConfig.customFields || []).filter(x => x.required).map(x => x.key);
    const emptyCustomFields = requiredCustomFieldKeys.filter(x => (params.customFieldValuesMap[x] || '').trim().length === 0);
    const hasEmptyRequiredCustomFields = emptyCustomFields.length > 0;

    const nameIsEmpty = !delivery.selectedOnsite && orderFormFieldsConfig.requireNameField && params.name.length === 0;
    const phoneIsEmpty = !delivery.selectedOnsite && orderFormFieldsConfig.requirePhoneField && params.phone.length === 0;
    const phoneIsWrong = !delivery.selectedOnsite && orderFormFieldsConfig.requirePhoneField && !phoneIsEmpty && (params.phone.length < 5 || params.phone.length > 20);
    const commentIsEmpty = orderFormFieldsConfig.requireCommentField && params.comment.length === 0;
    const deliveryRegionIsEmpty = delivery.selectedDelivery && (orderConfig.deliveryRegions?.length || 0) > 0 && params.deliveryRegion.length === 0;

    const addressIsEmpty = delivery.selectedDelivery && orderFormFieldsConfig.requireAddressField && params.address.length === 0;
    const pickupPointIsEmpty = delivery.selectedPickup && orderConfig.pickupEnabled && !delivery.selectedPickupPoint;

    return {
        _valid: !nameIsEmpty && !phoneIsEmpty && !phoneIsWrong && !addressIsEmpty && !commentIsEmpty && !pickupPointIsEmpty && !deliveryRegionIsEmpty && !hasEmptyRequiredCustomFields,
        nameIsEmpty,
        phoneIsEmpty,
        phoneIsWrong,
        commentIsEmpty,
        deliveryRegionIsEmpty,
        addressIsEmpty,
        pickupPointIsEmpty,
        customFieldErrorsMap: hasEmptyRequiredCustomFields ? Object.fromEntries(emptyCustomFields.map(x => ([x, true]))) : {}
    };
}

export async function submitOrder (params) {

    params.name = (params.name || '').trim();
    params.phone = (params.phone || '').trim();
    params.address = (params.address || '').trim();
    params.comment = (params.comment || '').trim();
    params.deliveryRegion = (params.deliveryRegion || '').trim();

    const orderFormFieldsConfig = get(orderFormFieldsStore);
    const delivery = get(deliveryStore);

    const formValidation = validateOrderForm(params, orderConfig, orderFormFieldsConfig, delivery);

    if (formValidation._valid) {

        const items = get(cartItemsStore);
        const total = get(cartTotalStore);

        const deliveryByRegion = getDeliveryPriceByRegion(delivery, params.deliveryRegion, orderConfig);
        const deliveryByTotal = getDeliveryPriceByTotal(total, orderConfig);

        const order = getOrder(items, total, params, delivery, deliveryByRegion, deliveryByTotal);
        const tenantKey = get(tenantKeyStore);

        order.apiKey = orderConfig.apiKey;
        const url = orderConfig.apiUrl;

        try {

            clearOrderPlacementResults();

            const makeRequest = async (attempt = 0) => {

                const endpoint = url + (url[url.length - 1] === '/' ? '' : '/') + tenantKey;
                const res = await fetch(endpoint, {
                    method: 'POST',
                    mode: 'cors',
                    body: JSON.stringify(order),
                    headers: {'content-type': 'application/json'}
                });

                switch (res.status) {

                    /// SUCCESS
                    case 200:
                    case 201:
                        const result = await res.json();
                        // console.log('Place order result', result);
                        if (result?.success) {
                            setOrderPlacementResults({orderNumber: result.orderNumber}, undefined);
                            reachGoal('ORDER_PLACED');
                            eventBus.set([{event: 'purchase', payload: {items, revenue: total?.sum, orderNumber: result.orderNumber}}]);
                        }
                        else {
                            setOrderPlacementResults({orderNumber: result.orderNumber}, {message: result.error?.message});
                            reachGoal('ORDER_FAILED');
                        }
                        closeOrderScreen();
                        clearCart();
                        break;

                    /// THROTTLING
                    case 429:
                    case 503:

                        /// Retry 4 times (0, 1, 2, 3)
                        if (attempt < 4) {

                            const delay = 2000 + 2000 * attempt * attempt; /// 2+4+10+20=36s
                            // const delay = 2000 + 2000 * attempt; /// 2+4+6+8=20s

                            console.log('delay', delay);
                            reachGoal(`ORDER_RETRY_${res.status}`);

                            return await new Promise(res => {
                                setTimeout(async () => {
                                    await makeRequest(attempt + 1);
                                    res();
                                }, delay);
                            });
                        }

                        setOrderPlacementResults({}, {status: res.status, message: instantTranslate('order.error.placement') + ` (${res.status})`});
                        closeOrderScreen();
                        reachGoal(`ORDER_FAILED_${res.status}`);
                        break;

                    /// ERRORS
                    case 404:
                    case 500:
                    default:
                        setOrderPlacementResults({}, {status: res.status, message: instantTranslate('order.error.placement') + ` (${res.status})`});
                        closeOrderScreen();
                        reachGoal('ORDER_FAILED');
                        break;
                }
            };

            await makeRequest(0);
        }
        catch (e) {
            setOrderPlacementResults({}, {status: 'Exception', message: e.message});
            closeOrderScreen();
            reachGoal('ORDER_EXCEPTION');
        }

        openOrderPlacementResultScreen();

        return {
            success: true
        };
    }
    else {

        console.log('Order Form Validation', formValidation);

        return {
            success: false,
            error: {
                nameIsEmpty: formValidation.nameIsEmpty,
                phoneIsEmpty: formValidation.phoneIsEmpty,
                deliveryRegionIsEmpty: formValidation.deliveryRegionIsEmpty,
                phoneIsWrong: formValidation.phoneIsWrong,
                addressIsEmpty: formValidation.addressIsEmpty,
                commentIsEmpty: formValidation.commentIsEmpty,
                pickupPointIsEmpty: formValidation.pickupPointIsEmpty,
                customFieldErrorsMap: formValidation.customFieldErrorsMap
            }
        };
    }
}
